import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useMemo, useState } from "react";
import { isEqual, sortBy } from "lodash";

import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useSnackbar } from "~/hooks/useSnackbar";
import { calculateDuration, getServiceDefaults } from "~/utils/visitsHelper";
import FilterGridRow from "~/components/scheduler/FilterGridRow";
import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import CareRequirementsEditor from "~/components/patient/CareRequirementsEditor";
import { getCareRequirementDetailedSummaryLabel } from "~/components/patient/careRequirementSummary";
import {
  CareRequirement,
  careRequirementFromTemplateRequirement,
  careRequirementToClinicalConstraintTemplateRequirementInput,
  cloneCareRequirement,
  createNewCareRequirement,
  getDefaultCareRequirementDateRange,
} from "@caredna-marlin/utils/client";
import {
  GetClinicalConstraintTemplatesForConfigQuery,
  useDeleteClinicalConstraintTemplateMutation,
  useUpdateClinicalConstraintTemplateMutation,
} from "~/graphql/__generated__/graphql";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

type Props = {
  template: GetClinicalConstraintTemplatesForConfigQuery["clinicalConstraintTemplate"][number];
  onDeleted: () => void;
};

type TemplateFrequency = CareRequirement["rruleFreq"];

type TemplateFormState = {
  id: number;
  serviceLineId: number;
  name: string;
  description: string;
  rruleFreq: TemplateFrequency;
  durationDays: number;
  firstVisitTypeId: number | null;
  firstVisitServiceIds: number[];
  firstVisitDuration: number | null;
  careRequirements: CareRequirement[];
};

const fieldWidth = 240;
const TEMPLATE_FREQUENCY: CareRequirement["rruleFreq"] = "daily";

const mapTemplateToFormState = (template: Props["template"]): TemplateFormState => {
  const templateFreq: TemplateFrequency = TEMPLATE_FREQUENCY;
  const { admissionDate, expectedDischargeDate } = getDefaultCareRequirementDateRange();

  const careRequirements: CareRequirement[] = (template.requirements ?? []).map((req) =>
    careRequirementFromTemplateRequirement({
      requirement: req,
      frequency: templateFreq,
      admissionDate,
      expectedDischargeDate,
    })
  );

  return {
    id: template.id,
    serviceLineId: template.serviceLineId,
    name: template.name ?? "",
    description: template.description ?? "",
    rruleFreq: templateFreq,
    durationDays: template.durationDays,
    firstVisitTypeId: template.firstVisitTypeId ?? null,
    firstVisitServiceIds: template.firstVisitServiceIds ?? [],
    firstVisitDuration: template.firstVisitDuration ?? null,
    careRequirements,
  };
};

export default function ClinicalConstraintTemplate({ template, onDeleted }: Props) {
  const orgConfig = useOrgConfig();
  const { setErrorMessage } = useSnackbar();
  const { timeLabel } = useZonedDateFormat();

  const baseState = useMemo(() => mapTemplateToFormState(template), [template]);
  const [local, setLocal] = useState<TemplateFormState>(baseState);

  useEffect(() => {
    setLocal(baseState);
  }, [baseState]);

  const [updateTemplate, { loading: updating }] = useUpdateClinicalConstraintTemplateMutation({
    awaitRefetchQueries: true,
    refetchQueries: ["GetClinicalConstraintTemplatesForConfig"],
  });

  const [deleteTemplate, { loading: deleting }] = useDeleteClinicalConstraintTemplateMutation({
    awaitRefetchQueries: true,
    refetchQueries: ["GetClinicalConstraintTemplatesForConfig"],
  });

  const saving = updating || deleting;
  const noLocalChanges = isEqual(local, baseState);
  const hasInvalidRows = local.careRequirements.some((cr) => !cr.visitTypeId);
  const serviceLineName = orgConfig.serviceLines[local.serviceLineId]?.name
    ?? `Service Line ${local.serviceLineId}`;

  // Visit types selectable as the first / start-of-care visit for this
  // template's service line.
  const firstVisitTypeOptions = useMemo(() => {
    const serviceLine = orgConfig.serviceLines[local.serviceLineId];
    return sortBy(
      (serviceLine?.visitTypeServiceLines ?? [])
        .map((vtsl) => orgConfig.visitTypes[vtsl.visitTypeId])
        .filter((vt): vt is NonNullable<typeof vt> => !!vt)
        .map((vt) => ({ id: vt.id, name: vt.name })),
      (vt) => vt.name ?? ""
    );
  }, [orgConfig.serviceLines, orgConfig.visitTypes, local.serviceLineId]);

  // Services associated with the selected first visit type. The first-visit
  // services picker is only shown when the chosen visit type has services.
  const firstVisitServiceOptions = useMemo(() => {
    if (local.firstVisitTypeId == null) { return []; }
    const visitType = orgConfig.visitTypes[local.firstVisitTypeId];
    return (visitType?.visitTypeServices ?? [])
      .map(({ service }) => service)
      .filter((service): service is NonNullable<typeof service> => !!service)
      .map((service) => ({ key: service.id, label: service.name ?? "" }));
  }, [orgConfig.visitTypes, local.firstVisitTypeId]);

  const onSave = async () => {
    const requirements = local.careRequirements.map((cr) => ({
      id: cr.id ?? null,
      ...careRequirementToClinicalConstraintTemplateRequirementInput(cr),
    }));

    await updateTemplate({
      variables: {
        id: local.id,
        name: local.name,
        description: local.description || null,
        rruleFreq: TEMPLATE_FREQUENCY,
        durationDays: local.durationDays,
        firstVisitTypeId: local.firstVisitTypeId,
        firstVisitServiceIds: local.firstVisitServiceIds,
        firstVisitDuration: local.firstVisitDuration,
        requirements,
      },
    });
  };

  const onDelete = async () => {
    try {
      const result = await deleteTemplate({
        variables: { id: local.id },
        update: (cache, { data }) => {
          const deletedId = data?.deleteClinicalConstraintTemplate?.id;
          if (!deletedId) { return; }
          cache.evict({ id: cache.identify({ __typename: "ClinicalConstraintTemplate", id: deletedId }) });
          cache.evict({ id: "ROOT_QUERY", fieldName: "clinicalConstraintTemplate" });
          cache.gc();
        },
      });

      const deleted = result.data?.deleteClinicalConstraintTemplate?.deleted;
      if (!deleted) {
        setErrorMessage("Template could not be deleted. Please try again.");
        return;
      }

      onDeleted();
    } catch (error) {
      console.error(error);
      setErrorMessage("Template could not be deleted. Please try again.");
    }
  };

  return (
    <Card sx={{ height: "100%", pl: 1 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {local.name || "Untitled Template"}
        </Typography>

        <Grid container rowSpacing={2} columnSpacing={2} sx={{ alignItems: "center" }}>
          <Grid size={12}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Service Line: {serviceLineName}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Grid container rowSpacing={2} columnSpacing={2} sx={{ alignItems: "center" }}>
              <FilterGridRow label="Template Name">
                <TextField
                  size="small"
                  fullWidth
                  value={local.name}
                  onChange={(e) => setLocal({ ...local, name: e.target.value })}
                />
              </FilterGridRow>

              <FilterGridRow label="Description">
                <TextField
                  size="small"
                  fullWidth
                  value={local.description}
                  onChange={(e) => setLocal({ ...local, description: e.target.value })}
                />
              </FilterGridRow>

              <FilterGridRow label="Duration (days)">
                <TextField
                  type="number"
                  size="small"
                  sx={{ width: fieldWidth }}
                  value={local.durationDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (Number.isFinite(value) && value >= 1) {
                      setLocal((prev) => ({ ...prev, durationDays: value }));
                    }
                  }}
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </FilterGridRow>

              <FilterGridRow label="First visit type">
                <FormControl size="small" sx={{ width: fieldWidth }}>
                  <Select
                    displayEmpty
                    value={local.firstVisitTypeId ?? 0}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box component="span" sx={{ color: "text.disabled" }}>None</Box>;
                      }
                      return firstVisitTypeOptions.find((vt) => vt.id === selected)?.name ?? selected;
                    }}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const nextId = Number.isFinite(value) && value !== 0 ? value : null;
                      setLocal((prev) => {
                        // No change: keep the saved services/duration as-is.
                        if (nextId === prev.firstVisitTypeId) { return prev; }
                        if (nextId == null) {
                          return { ...prev, firstVisitTypeId: null, firstVisitServiceIds: [], firstVisitDuration: null };
                        }
                        // New visit type: seed its default services and default
                        // (services + resource requirement) duration, mirroring
                        // how selecting a visit type behaves elsewhere.
                        const defaultServiceIds = getServiceDefaults(orgConfig, nextId);
                        return {
                          ...prev,
                          firstVisitTypeId: nextId,
                          firstVisitServiceIds: defaultServiceIds.map(Number),
                          firstVisitDuration: Number(calculateDuration(orgConfig, nextId, defaultServiceIds)),
                        };
                      });
                    }}
                  >
                    <MenuItem value={0}><em>None</em></MenuItem>
                    {firstVisitTypeOptions.map((vt) => (
                      <MenuItem key={vt.id} value={vt.id}>{vt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FilterGridRow>

              {firstVisitServiceOptions.length > 0 && (
                <FilterGridRow label="First visit services">
                  <FormControl size="small" sx={{ width: fieldWidth }}>
                    <CheckboxSelect
                      value={local.firstVisitServiceIds}
                      onChange={(serviceIds) => setLocal((prev) => ({
                        ...prev,
                        firstVisitServiceIds: serviceIds,
                        // Keep the duration in sync with the selected services'
                        // durations (plus the visit type's resource requirements).
                        firstVisitDuration: prev.firstVisitTypeId != null
                          ? Number(calculateDuration(orgConfig, prev.firstVisitTypeId, serviceIds.map(String)))
                          : prev.firstVisitDuration,
                      }))}
                      options={firstVisitServiceOptions}
                      fullWidth
                      size="small"
                    />
                  </FormControl>
                </FilterGridRow>
              )}

              {local.firstVisitTypeId != null && (
                <FilterGridRow label="First visit duration (min)">
                  <TextField
                    type="number"
                    size="small"
                    sx={{ width: fieldWidth }}
                    value={local.firstVisitDuration ?? ""}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      setLocal((prev) => ({
                        ...prev,
                        firstVisitDuration: Number.isFinite(value) && value >= 0 ? value : null,
                      }));
                    }}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </FilterGridRow>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <CareRequirementsEditor
            careRequirements={local.careRequirements}
            setCareRequirements={(updater) => {
              setLocal((prev) => ({
                ...prev,
                careRequirements: updater(prev.careRequirements),
              }));
            }}
            serviceLineId={local.serviceLineId}
            createNewRequirement={() => createNewCareRequirement({
              frequency: TEMPLATE_FREQUENCY,
            })}
            duplicateRequirement={(requirement) => cloneCareRequirement(requirement)}
            summaryLabel={(careRequirement, index) => getCareRequirementDetailedSummaryLabel({
              careRequirement,
              index,
              visitTypeName: orgConfig.visitTypes[careRequirement.visitTypeId]?.name,
              timeLabel,
            })}
            mode="openNewRows"
            contentPaddingX={1}
          />
        </Box>
      </CardContent>

      <CardActions>
        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={saving || noLocalChanges || !local.name || hasInvalidRows}
          onClick={onSave}
          loading={updating}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          disabled={saving}
          onClick={() => setLocal(baseState)}
        >
          Reset
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          size="small"
          color="error"
          disabled={saving}
          onClick={onDelete}
          loading={deleting}
        >
          Delete Template
        </Button>
      </CardActions>
    </Card>
  );
}
